import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useEffect, useState, useRef } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  useWorkflowPresets,
  useWorkflowPreset,
  useUpdateWorkflowPreset,
  useCreateWorkflowPreset,
  useDeleteWorkflowPreset,
} from '../hooks/useWorkflowPresets';
import { WorkflowPresetEditor } from '../components/workflow/WorkflowPresetEditor';
import { uploadWorkflowPresetThumbnail } from '../lib/workflow-presets.api';

export const Route = createFileRoute('/w/$workspaceId/settings/workflow-presets')({
  component: WorkflowPresetsSettingsPage,
});

function WorkflowPresetsSettingsPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);

  const canAccessSettings = hasPermission('workflow_presets.update');

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canAccessSettings) {
      showPermissionDenied('Gerenciar Workflow Presets');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canAccessSettings, showPermissionDenied, navigate, workspaceId]);

  const { data: presets, isLoading } = useWorkflowPresets({
    enabled: canAccessSettings,
  });
  const updatePreset = useUpdateWorkflowPreset();
  const createPreset = useCreateWorkflowPreset();
  const deletePreset = useDeleteWorkflowPreset();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);

  const selectedPresetListItem =
    presets?.find((p) => p.id === selectedPresetId) ?? null;
  const { data: selectedPresetDetail } = useWorkflowPreset(
    selectedPresetId ?? undefined,
  );
  const selectedPreset = selectedPresetDetail ?? selectedPresetListItem ?? null;

  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [presetTags, setPresetTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!selectedPresetId && presets && presets.length > 0) {
      setSelectedPresetId(presets[0].id);
    }
  }, [presets, selectedPresetId]);

  useEffect(() => {
    if (!selectedPreset) {
      setPresetName('');
      setPresetDescription('');
      setPresetTags([]);
      return;
    }
    setPresetName(selectedPreset.name ?? '');
    setPresetDescription(selectedPreset.description ?? '');
    setPresetTags(selectedPreset.tags ?? []);
  }, [selectedPreset?.id, selectedPreset?.name, selectedPreset?.description]);

  const handleCreatePreset = () => {
    createPreset.mutate(
      {
        name: 'Novo Workflow Preset',
        description: '',
        calendarMode: 'CALENDAR_DAYS',
        graph: {
          nodes: [],
          edges: [],
        },
      },
      {
        onSuccess: (created: any) => {
          setSelectedPresetId(created.id);
        },
      },
    );
  };

  const handleImportJson = () => {
    const json = prompt('Cole aqui o JSON exportado de um Workflow Preset para importar este grafo.');
    if (!json) return;
    try {
      const parsed = JSON.parse(json);
      if (!parsed.graph || !parsed.name) {
        alert('JSON inválido: esperado objeto com campos "name" e "graph".');
        return;
      }
      createPreset.mutate(
        {
          name: parsed.name,
          description: parsed.description,
          calendarMode: parsed.calendarMode || 'CALENDAR_DAYS',
          graph: parsed.graph,
        },
        {
          onSuccess: (created: any) => {
            setSelectedPresetId(created.id);
          },
        },
      );
    } catch (error) {
      console.error('Erro ao importar Workflow Preset:', error);
      alert('JSON inválido. Verifique o conteúdo e tente novamente.');
    }
  };

  if (!canAccessSettings) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <p
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
                textAlign: 'center',
              }}
            >
              Você não tem permissão para acessar esta página.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
        <Breadcrumb
          items={[
            { label: 'Meu Workspace', to: `/w/${workspaceId}/clients` },
            { label: 'Configurações', to: `/w/${workspaceId}/settings/members` },
            { label: 'Workflow Presets' },
          ]}
        />

        <div
          className="bg-white p-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="flex flex-col gap-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-lg font-semibold text-gray-800">Workflow Presets</h1>
                <p className="text-xs text-gray-500 max-w-md">
                  Selecione um preset nos cards abaixo ou crie um novo. O canvas de edição ficará logo abaixo.
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                onClick={handleImportJson}
              >
                Importar JSON
              </button>
            </div>

            {isLoading && (
              <p className="text-sm text-gray-500">Carregando presets...</p>
            )}

            {!isLoading && (
              <>
                <div className="flex items-center gap-3 overflow-x-auto pb-1">
                  <button
                    type="button"
                    onClick={handleCreatePreset}
                    className="flex min-w-[220px] max-w-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-xs text-gray-600 hover:border-indigo-400 hover:bg-indigo-50/60 hover:text-indigo-700 transition"
                  >
                    <span className="mb-1 text-2xl leading-none">+</span>
                    <span className="font-semibold">Novo Workflow Preset</span>
                    <span className="mt-1 text-[10px] text-gray-500 text-center">
                      Clique para criar um novo fluxo visual do zero.
                    </span>
                  </button>

                  {presets && presets.length > 0 && (
                    <div className="flex gap-3">
                      {presets.map((preset) => {
                        const isSelected = selectedPresetId === preset.id;
                        return (
                          <div
                            key={preset.id}
                            className={`min-w-[220px] max-w-[220px] rounded-xl border px-4 py-3 text-xs cursor-pointer transition ${
                              isSelected
                                ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
                            }`}
                            onClick={() => setSelectedPresetId(preset.id)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="space-y-1">
                                <p className="text-[11px] font-semibold text-gray-800 truncate">
                                  {preset.name}
                                </p>
                                <p className="text-[10px] text-gray-500 line-clamp-2">
                                  {preset.description || 'Preset sem descrição.'}
                                </p>
                              </div>
                              <span className="ml-2 text-[10px] text-gray-500 whitespace-nowrap">
                                v{preset.version}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500">
                              <span>
                                {preset.calendarMode === 'BUSINESS_DAYS'
                                  ? 'Dias úteis'
                                  : 'Dias corridos'}
                              </span>
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  className="rounded-full border border-gray-200 bg-white px-2 py-0.5 hover:bg-gray-50"
                                  title="Duplicar preset"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    createPreset.mutate(
                                      {
                                        name: `${preset.name} (cópia)`,
                                        description: preset.description || undefined,
                                        calendarMode: preset.calendarMode,
                                        graph: preset.graph,
                                      },
                                      {
                                        onSuccess: (created: any) => {
                                          setSelectedPresetId(created.id);
                                        },
                                      },
                                    );
                                  }}
                                >
                                  Duplicar
                                </button>
                                <button
                                  type="button"
                                  className="rounded-full border border-gray-200 bg-white px-2 py-0.5 hover:bg-gray-50"
                                  title="Exportar como JSON"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const payload = {
                                      id: preset.id,
                                      name: preset.name,
                                      description: preset.description,
                                      calendarMode: preset.calendarMode,
                                      graph: preset.graph,
                                    };
                                    const blob = new Blob([JSON.stringify(payload, null, 2)], {
                                      type: 'application/json',
                                    });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `workflow-preset-${preset.name}.json`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    URL.revokeObjectURL(url);
                                  }}
                                >
                                  JSON
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-4 space-y-3">
                  {selectedPreset ? (
                    <>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-700">
                              Nome do preset
                            </label>
                            <input
                              type="text"
                              className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              value={presetName}
                              onChange={(e) => setPresetName(e.target.value)}
                              placeholder="Ex: Fluxo padrão de contestação"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-700">
                              Descrição
                            </label>
                            <textarea
                              className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 min-h-[50px] text-[11px] resize-none"
                              value={presetDescription}
                              onChange={(e) => setPresetDescription(e.target.value)}
                              placeholder="Use este espaço para explicar quando este preset deve ser usado."
                            />
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-2 text-[11px] text-gray-600">
                              <input
                                type="checkbox"
                                className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                checked={selectedPreset?.isActive ?? true}
                                onChange={(e) => {
                                  if (!selectedPresetId) return;
                                  updatePreset.mutate({
                                    id: selectedPresetId,
                                    payload: {
                                      isActive: e.target.checked,
                                    },
                                  });
                                }}
                              />
                              <span>
                                {selectedPreset?.isActive ? 'Workflow ativo' : 'Workflow inativo'}
                              </span>
                            </label>
                          </div>
                          <button
                            type="button"
                            className="inline-flex items-center rounded-full bg-indigo-600 px-4 py-1.5 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                            disabled={!presetName.trim() || updatePreset.isPending}
                            onClick={() => {
                              if (!selectedPresetId) return;
                              updatePreset.mutate({
                                id: selectedPresetId,
                                payload: {
                                  name: presetName.trim(),
                                  description: presetDescription || null,
                                  tags: presetTags,
                                },
                              });
                            }}
                          >
                            {updatePreset.isPending ? 'Salvando...' : 'Salvar preset'}
                          </button>
                          <button
                            type="button"
                            className="text-[11px] text-red-500 hover:text-red-600"
                            onClick={() => {
                              if (!selectedPresetId) return;
                              if (
                                !window.confirm(
                                  'Tem certeza que deseja apagar este Workflow Preset? Esta ação não pode ser desfeita.',
                                )
                              ) {
                                return;
                              }
                              deletePreset.mutate(selectedPresetId, {
                                onSuccess: () => {
                                  setSelectedPresetId(null);
                                },
                              });
                            }}
                          >
                            Apagar preset
                          </button>
                        </div>
                      </div>

                      <WorkflowPresetEditor
                        value={selectedPreset.graph ?? null}
                        onChange={(graph) => {
                          if (!selectedPresetId) return;
                          updatePreset.mutate({
                            id: selectedPresetId,
                            payload: {
                              graph,
                            },
                          });
                        }}
                      />
                      <div className="mt-4 flex flex-wrap gap-6 items-start">
                        <div className="flex items-start gap-3">
                          <div className="w-20 h-20 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                            {selectedPreset?.thumbnailUrl ? (
                              <img
                                src={selectedPreset.thumbnailUrl}
                                alt="Miniatura do workflow"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-[10px] text-gray-400 text-center px-2">
                                Sem miniatura
                              </span>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-[11px] font-medium text-gray-700">
                              Miniatura do workflow
                            </p>
                            <p className="text-[10px] text-gray-500">
                              Use uma imagem pequena para identificar visualmente este preset.
                            </p>
                            <div className="flex gap-2">
                              <input
                                ref={thumbnailInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (event) => {
                                  const file = event.target.files?.[0];
                                  if (!file || !selectedPresetId) return;
                                  setThumbnailUploading(true);
                                  try {
                                    await uploadWorkflowPresetThumbnail(
                                      selectedPresetId,
                                      file,
                                    );
                                  } catch (error) {
                                    console.error(
                                      'Erro ao enviar miniatura do workflow:',
                                      error,
                                    );
                                    alert(
                                      'Erro ao enviar imagem. Tente novamente.',
                                    );
                                  } finally {
                                    setThumbnailUploading(false);
                                    if (thumbnailInputRef.current) {
                                      thumbnailInputRef.current.value = '';
                                    }
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                onClick={() => thumbnailInputRef.current?.click()}
                                disabled={thumbnailUploading}
                              >
                                {thumbnailUploading
                                  ? 'Enviando...'
                                  : selectedPreset?.thumbnailUrl
                                  ? 'Alterar miniatura'
                                  : 'Adicionar miniatura'}
                              </button>
                              {selectedPreset?.thumbnailUrl && (
                                <button
                                  type="button"
                                  className="text-[11px] text-red-500 hover:text-red-600"
                                  disabled={updatePreset.isPending}
                                  onClick={() => {
                                    if (!selectedPresetId) return;
                                    updatePreset.mutate({
                                      id: selectedPresetId,
                                      payload: {
                                        thumbnailUrl: null,
                                      },
                                    });
                                  }}
                                >
                                  Remover
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 min-w-[220px]">
                          <p className="text-[11px] font-medium text-gray-700 mb-1">
                            Tags do workflow
                          </p>
                          <p className="text-[10px] text-gray-500 mb-2">
                            Use tags livres para facilitar a busca e identificar o tipo de fluxo.
                          </p>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {presetTags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-[10px] text-gray-700 border border-gray-200"
                              >
                                {tag}
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-gray-600"
                                  onClick={() =>
                                    setPresetTags((current) =>
                                      current.filter((t) => t !== tag),
                                    )
                                  }
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            {presetTags.length === 0 && (
                              <span className="text-[10px] text-gray-400">
                                Nenhuma tag adicionada ainda.
                              </span>
                            )}
                          </div>
                          <input
                            type="text"
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                const value = newTagInput.trim();
                                if (
                                  value &&
                                  !presetTags.includes(value.toLowerCase())
                                ) {
                                  setPresetTags((current) => [
                                    ...current,
                                    value,
                                  ]);
                                }
                                setNewTagInput('');
                              }
                            }}
                            placeholder="Digite uma tag e pressione Enter"
                            className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 mt-6 text-center">
                      Selecione um preset nos cards acima para editar o fluxo visual.
                    </p>
                  )}
                </div>

                {!presets || presets.length === 0 ? (
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    Nenhum Workflow Preset cadastrado ainda. Clique no card &quot;Novo Workflow
                    Preset&quot; para começar.
                  </p>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

